/**
 * `ad-policy` — from app configuration to the gate's capping.
 *
 * Ad capping used to be a constant (`CHILD_AD_CAP_POLICY`):
 * changing "2 interstitials per day" required touching code and publishing a version.
 * Here it becomes configuration of the TWO layers this app really has —
 * build env (platform) and adult settings behind the parental gate
 * (`localStorage`) — without inventing a back-office that the game does not use.
 *
 * **The child policy is still the FLOOR.** Configuration can only
 * TIGHTEN `CHILD_AD_CAP_POLICY`: an env with `VITE_ADS_INTERSTITIAL_PER_DAY=9`
 * does not raise the ceiling of the §6 regime, just as `VITE_ADS_CHILD_DIRECTED=false` does not
 * turn off the K-tier regime. Configuration is a control to give FEWER
 * ads, never more.
 *
 * It is read with a *getter* on each gate evaluation, not once at startup:
 * so the adult's change is applied **on the fly**, without restarting the game.
 */

import { readParentSettings, resolveAppConfig } from "@melodimals/config/app-config";

import { CHILD_AD_CAP_POLICY } from "./growth/gate";
import type { AdCapPolicy } from "./growth/types";

const MINUTE_MS = 60_000;

/** Part of the capping that this app exposes as configuration. */
type ConfigurableAdCaps = Pick<
  AdCapPolicy,
  "interstitialPerSession" | "interstitialPerDay" | "minGapBetweenInterstitialsMs"
>;

/**
 * Effective capping: app configuration, clipped to the child regime's
 * floor. Fewer ads and more separation, never the other way around.
 */
export function adCapsFromConfig(
  env: Record<string, string | undefined> = {},
  storage?: Pick<Storage, "getItem">,
): ConfigurableAdCaps {
  const { adCaps } = resolveAppConfig(env, readParentSettings(storage));
  return {
    interstitialPerSession: Math.min(
      CHILD_AD_CAP_POLICY.interstitialPerSession,
      adCaps.interstitialPerSession,
    ),
    interstitialPerDay: Math.min(CHILD_AD_CAP_POLICY.interstitialPerDay, adCaps.interstitialPerDay),
    minGapBetweenInterstitialsMs: Math.max(
      CHILD_AD_CAP_POLICY.minGapBetweenInterstitialsMs,
      adCaps.minGapMinutes * MINUTE_MS,
    ),
  };
}
